// api.js
import { clearAuthSession, getAccessToken, setAuthSession } from "./authSession";
import { API_URL } from "./env";

class Api {
  constructor(config) {
    this._url = (config.url || "").replace(/\/+$/, "");
  }

  _join(path) {
    return path.startsWith("/") ? `${this._url}${path}` : `${this._url}/${path}`;
  }

  async _refreshToken() {
    const res = await fetch(this._join("/refresh"), {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) throw new Error("Unable to refresh");

    const data = await res.json();
    if (data?.accessToken) {
      setAuthSession({
        accessToken: data.accessToken,
        accessTokenExpiration: data.accessTokenExpiration,
      });
    } else {
      clearAuthSession();
      throw new Error("No accessToken in refresh response");
    }
  }

  async _request(path, { method = "GET", headers = {}, body, retry = true } = {}) {
    const token = getAccessToken();
    const finalHeaders = {
      ...(body instanceof FormData ? {} : { "content-type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    };

    const res = await fetch(this._join(path), {
      method,
      headers: finalHeaders,
      body,
      credentials: "include",
    });

    if (res.status === 401 && retry) {
      try {
        await this._refreshToken();
        return this._request(path, { method, headers, body, retry: false });
      } catch {
        throw new Error("Unauthorized");
      }
    }

    if (!res.ok) {
      let errorMessage = res.statusText || "Произошла ошибка";

      try {
        const contentType = res.headers.get("content-type") || "";

        if (contentType.includes("application/json")) {
          const errorData = await res.json();
          errorMessage = errorData?.message || errorData?.error || errorMessage;
        } else {
          const text = await res.text();
          if (text) {
            try {
              const parsed = JSON.parse(text);
              errorMessage = parsed?.message || parsed?.error || text;
            } catch {
              errorMessage = text;
            }
          }
        }
      } catch {
        // use fallback message
      }

      throw new Error(errorMessage);
    }

    const text = await res.text().catch(() => "");
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  // ---- PUBLIC API ----

  async uploadLessonImage(lessonId, file) {
    const body = new FormData();
    body.append("image", file);
    const result = await this._request(`/lesson/${encodeURIComponent(lessonId)}/images`, {
      method: "POST",
      body,
    });
    let url;
    try {
      url = new URL(result?.url);
    } catch {
      throw new Error("Сервер не вернул корректную ссылку на изображение.");
    }
    if (!["https:", "http:"].includes(url.protocol)) {
      throw new Error("Сервер вернул недопустимую ссылку на изображение.");
    }
    return url.href;
  }

  addUnit(dataUnit) {
    return this._request(`/unit`, {
      method: "POST",
      body: JSON.stringify(dataUnit),
    });
  }

  getAllUnits() {
    return this._request(`/unit`);
  }

  getOneUnit(unitId) {
    return this._request(`/unit/${unitId}`);
  }

  updateUnit(unitId, data) {
    return this._request(`/unit/${unitId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  deleteUnit(unitId) {
    return this._request(`/unit/${unitId}`, { method: "DELETE" });
  }

  addLesson(title, markdown, markdownSpare, video, unit, hasHomework) {
    return this._request(`/lesson`, {
      method: "POST",
      body: JSON.stringify({
        title,
        markdown,
        markdownSpare,
        video,
        unit,
        hasHomework,
      }),
    });
  }

  getAllLessonUnits(unit) {
    return this._request(`/lesson/unit/${unit}`);
  }

  getLesson() {
    return this._request(`/lesson`);
  }

  getOneLesson(lessonId) {
    return this._request(`/lesson/${lessonId}`);
  }

  deleteLesson(lessonId) {
    return this._request(`/lesson/${lessonId}`, { method: "DELETE" });
  }

  editLesson(lessonID, { title, markdown, video, hasHomework }) {
    return this._request(`/lesson/${lessonID}`, {
      method: "PATCH",
      body: JSON.stringify({ title, markdown, video, hasHomework }),
    });
  }

  getQuestionLesson(lessonId) {
    return this._request(`/question/lesson/${lessonId}`);
  }

  // Диалоги
  getDialogsLesson(lessonId) {
    return this._request(`/dialogue/${lessonId}/lesson`);
  }

  changeDialogueProgress(dialogueId, currentUser) {
    return this._request(`/dialogue/${dialogueId}/progress`, {
      method: "PUT",
      body: JSON.stringify({
        email: currentUser.email,
        name: currentUser.name,
        idUser: currentUser._id,
      }),
    });
  }

  addDialogue(data) {
    return this._request(`/dialogue`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  updateDialogue(id, payload) {
    return this._request(`/dialogue/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  createQuestion({ text, answerOptions, correctAnswer, testId }) {
    return this._request(`/question`, {
      method: "POST",
      body: JSON.stringify({ text, answerOptions, correctAnswer, testId }),
    });
  }

  updateQuestion(questionId, { text, answerOptions, correctAnswer }) {
    return this._request(`/question/${questionId}`, {
      method: "PUT",
      body: JSON.stringify({ text, answerOptions, correctAnswer }),
    });
  }

  recordTestResponse({ answerUser, testId, idUser }) {
    return this._request(`/question/${testId}/progress`, {
      method: "PUT",
      body: JSON.stringify({ answerUser, idUser }),
    });
  }

  getHomeworkResult(currentUserId, lessonId) {
    return this._request(`/homework/lesson/${lessonId}/user/${currentUserId}`);
  }

  createHomework(currentUserId, lessonId, homeworkUrl) {
    return this._request(`/homework`, {
      method: "POST",
      body: JSON.stringify({ userId: currentUserId, lessonId, cloudUrl: homeworkUrl }),
    });
  }

  updateHomeworkLink(lessonId, homeworkUrl) {
    return this._request(`/homework/lesson/${lessonId}/user`, {
      method: "PATCH",
      body: JSON.stringify({ cloudUrl: homeworkUrl }),
    });
  }

  recordingUserCompletedLesson(currentUserId, lessonId) {
    return this._request(`/lesson/${lessonId}/passed`, {
      method: "PATCH",
      body: JSON.stringify({ idUser: currentUserId }),
    });
  }

  // ?? ДОБАВЛЕНО: Создание платежа
  createPayment(payload) {
    return this._request(`/api/payments`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // ?? ДОБАВЛЕНО: Получение статуса платежа
  getPaymentStatus(paymentId) {
    return this._request(`/api/payments/${paymentId}`, {
      method: "GET",
    });
  }

  // ?? получить мои платежи (по умолчанию только succeeded)
  getMyPayments(status = "succeeded") {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return this._request(`/api/payments/my${qs}`);
  }
}

// базовый адрес из env
const API_BASE = API_URL.replace(/\/+$/, "");
export const api = new Api({ url: API_BASE });
